import { useGetAddress } from '@/api/address';
import { AutoComplete } from '../Common/AutoComplete';
import { useState, useEffect, useRef, useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useCalculateTax } from '@/api/tax';
import { formatCanadianPostalCode } from '@/utils/zipCode';
import { useTranslate } from '@/hooks/useTranslate';

interface Props {
  value: string;
  onChange: (value: string) => void;
}

export const UnmanagedAddress = ({ value, onChange }: Props) => {
  const {
    actions: { updatePriceBreakdown, updateAddressDetails },
    addressDetails,
    customer,
  } = useContext(CheckoutContext);
  const [addressList, setAddressList] = useState<{ label: string; value: string }[]>([]);
  const { mutateAsync: getAddress, isPending } = useGetAddress();
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { mutateAsync: calculateTax } = useCalculateTax();
  const { t } = useTranslate();
  const countryCode = customer?.taxCountry ?? customer?.geoLocator?.countryCode2;
  const isCanada = countryCode === 'CA';

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    if (!value.trim()) {
      setAddressList([]);
      return;
    }

    debounceTimeout.current = setTimeout(async () => {
      try {
        const { addressResults } = await getAddress(value);
        setAddressList(addressResults.map((addr) => ({ label: addr.address, value: addr.address })));
      } catch (error) {
        console.error('Error fetching addresses:', error);
        setAddressList([]);
      }
    }, 500);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [value, getAddress]);

  const handleChange = (newValue: string) => {
    onChange(newValue);
  };

  const handleSelect = async () => {
    const { priceBreakdown, zipCode } = await calculateTax({ address: value, zipCode: addressDetails?.zipCode ?? '' });
    // Format Canadian postal code if needed
    const formattedZipCode = zipCode && isCanada ? formatCanadianPostalCode(zipCode) : (zipCode ?? '');
    updateAddressDetails({ address: value, zipCode: formattedZipCode });
    updatePriceBreakdown(priceBreakdown);
  };

  return (
    <div className="w-full">
      <AutoComplete
        onInputChange={handleChange}
        options={addressList}
        onSelect={handleSelect}
        value={''}
        placeholder={t('address')}
        className={'w-full'}
        error={''}
        disabled={false}
        isLoading={isPending}
        disableFiltering={true}
      />
    </div>
  );
};
